import React from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import Sidebar from '../../../Sidebar';
import { useDeviationViewContext } from '../../../Context/DeviationViewContext';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import SubPaywall from '../../../DeviationViews/Full/_partials/SubPaywall';
import { getDeviationType } from '../../../DeviationViews/_helpers';
import { DeviationTypes } from '../../../types/deviation';
import {
  getCommentableTypeForDeviationType,
  isExtendedDeviationFullyLoaded,
} from '../../redux/helpers';
import Comments from '../Comments';
import TierUpsell from '../TierUpsell';
import SecondaryActionBarWrapper from './SecondaryActionBarWrapper';

import s from './DesktopLayout.scss';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
  stageContent: React.ReactNode;
  stageClassName?: string;
  metaContent: React.ReactNode;
  metaClassName?: string;
  backgroundContent?: React.ReactNode;
  backgroundClassName?: string;
  withSecondaryActionBar?: boolean;
}

const DesktopLayout: React.FC<React.PropsWithChildren<Props>> = ({
  stageContent,
  metaContent,
  className,
  stageClassName,
  metaClassName,
  backgroundContent,
  backgroundClassName,
  withSecondaryActionBar = false,
  deviation,
  deviation: { deviationId, typeId },
}) => {
  const { isTheater } = useDeviationViewContext();
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);

  const { tier, isTier, isSubscribedButBlocked, needsSubscription } =
    useSubscriptionTier(deviation);
  const isBlocked = needsSubscription || isSubscribedButBlocked;
  const isLongformText = [DeviationTypes.JOURNAL, DeviationTypes.LITERATURE].includes(
    getDeviationType(deviation)
  );
  const useLowerLeftColumnPaywall = isBlocked && isLongformText;
  const commentableTypeId = getCommentableTypeForDeviationType(deviation);

  // we -don't- show the tier upsell when locked-subscribed (desktop only)
  const showTierUpsell =
    (!!tier || isTier) && !useLowerLeftColumnPaywall && !isSubscribedButBlocked;

  const leftColumnTheaterClasses = classnames(
    s['left-column'],
    s['force-theater-full'],
    metaClassName
  );

  return (
    <div
      className={classnames(
        s['root'],
        isTheater && s['theater'],
        !backgroundContent && s['no-background'],
        className
      )}
    >
      {backgroundContent && (
        <div className={classnames(s['background'], backgroundClassName)}>{backgroundContent}</div>
      )}

      <SecondaryActionBarWrapper
        deviation={deviation}
        withSecondaryActionBar={withSecondaryActionBar && !isBlocked}
        className={leftColumnTheaterClasses}
      >
        <div className={classnames(s['art-stage'], stageClassName)}>{stageContent}</div>
        {!isTheater && (
          <Sidebar deviation={deviation} className={classnames(s['right-column'], s['sidebar'])} />
        )}
        <div className={leftColumnTheaterClasses}>{metaContent}</div>
      </SecondaryActionBarWrapper>

      {isTheater && (
        <Sidebar deviation={deviation} className={classnames(s['right-column'], s['sidebar'])} />
      )}

      <div className={s['left-column']}>
        {useLowerLeftColumnPaywall && (
          <SubPaywall className={s['paywall']} deviation={deviation} showImage={false} />
        )}
        {!isBlocked && (
          <>
            {isExtendedLoaded && (
              <Comments
                key={`comments-${commentableTypeId}:${deviationId}`}
                deviation={deviation}
                className={s['comments']}
              />
            )}
            {!isExtendedLoaded && <LoadingIndicator className={s['lloader']} view="llama" />}
          </>
        )}
        {showTierUpsell && <TierUpsell className={s['upsell']} deviation={deviation} />}
      </div>
      <div className={s['clear']} />
    </div>
  );
};

DesktopLayout.displayName = 'DesktopLayout';

export default DesktopLayout;
